import { stanceBodyGap } from './boss_body_envelope'
import { awokenBiteLandingGapPx } from './awoken_bite_mouth'
import type { AttackDef, Boss, Player } from './types'
import { arenaBoundsFor, BOSS, FIXED_DT } from './balance'
import { getBossAttackTravelMode } from './boss_attack_travel'
import { usesNeutralMovementTrial } from './boss_neutral_movement'

// DEV comparison: a planted attack has one committed destination for its whole combo.
// Geometry is a provisional torso envelope, not the image's weapon-inclusive bounds.
export const PLANTED_PROFILES = {
  'crowned-king-awoken': { attacks: ['feralBite', 'feralFrenzy'], front: 120, rear: 90, height: 125, contact: 140, load: 12 },
  'vigil-knight': { attacks: ['lanceThrust'], front: 30, rear: 30, height: 155, contact: 150, load: 14 },
} as const
export function plantedProfile(boss: Boss, attack = boss.currentAttack) {
  const p = PLANTED_PROFILES[boss.id as keyof typeof PLANTED_PROFILES]
  return p && attack && (p.attacks as readonly string[]).includes(attack.id)
    && !attack.passThroughBody && !attack.crossArena && !attack.positionEvadable
    && !attack.playerLethal && !attack.bossFinisher ? p : undefined
}
function inArena(x: number, boss: Boss) {
  const bounds = arenaBoundsFor(boss.id)
  return Math.max(bounds.leftWall, Math.min(bounds.rightWall, x))
}

export function preparePlantedTravel(boss: Boss, player: Player): Boss {
  const p = plantedProfile(boss)
  if (getBossAttackTravelMode() !== 'planted' || !p
      || !['telegraph', 'active', 'comboGap', 'recovery', 'beatGap'].includes(boss.state)) {
    if (!boss.plantedTravel) return boss
    const { plantedTravel: _old, ...rest } = boss
    return rest
  }
  const dir = boss.facing === 'right' ? 1 : -1
  // In the action trial, spacing belongs to explicit idle movement. A held
  // telegraph / inter-hit stance must not slide backwards to manufacture a lunge.
  const holdStance = usesNeutralMovementTrial(boss)
  let t = boss.plantedTravel
  if (!t || t.attackId !== boss.currentAttack!.id
      || (boss.state === 'telegraph' && (t.phase !== 'telegraph' || t.dir !== dir))) {
    // Make room for a small load-and-strike motion, once, without chasing each tick.
    const gap = (player.x - boss.x) * dir
    // 覚醒王の噛みは口の前方距離が構え (2026-09-26)。それ以外は従来の胴の前端。
    const mouth = awokenBiteLandingGapPx(boss, boss.currentAttack!)
    const load = !holdStance && gap >= 0 && gap < (mouth ?? p.contact) + p.load
      ? Math.max(p.load, (mouth ?? p.front + 15) + p.load - gap) : 0
    t = { attackId: boss.currentAttack!.id, dir, targetX: boss.x,
      loadX: inArena(boss.x - dir * load, boss), phase: boss.state, committed: false }
  }
  if (boss.state === 'active' && !t.committed) {
    t = { ...t, targetX: boss.lungeTargetX, committed: true }
  }
  if (boss.state === 'comboGap' && t.phase !== 'comboGap') {
    // Reload behind the reached stance; the next bite returns to the SAME stance.
    t = { ...t, targetX: boss.x, loadX: holdStance ? boss.x : inArena(boss.x - t.dir * p.load, boss) }
  }
  t = { ...t, phase: boss.state }
  let x = boss.x
  if (!holdStance && (boss.state === 'telegraph' || boss.state === 'comboGap')
      && (player.x - boss.x) * t.dir > 0) {
    // Never move toward a player who dodged behind. No correction/warp at phase end.
    const remaining = (boss.x - t.loadX) * t.dir
    if (remaining > 0) x = inArena(boss.x - t.dir * Math.min(remaining, 300 * FIXED_DT), boss)
  }
  return { ...boss, x, plantedTravel: t }
}

export function plantedTravelTarget(boss: Boss, player: Player, attack: AttackDef, lungeDistance: number): number | undefined {
  const p = plantedProfile(boss, attack)
  if (getBossAttackTravelMode() !== 'planted' || !p) return undefined
  const t = boss.plantedTravel
  if (t?.committed) return t.targetX
  const dir = boss.facing === 'right' ? 1 : -1
  // Plan once at commitment. This is not a per-tick wall against the player.
  const initialGap = t ? (player.x - t.targetX) * dir : p.contact
  // 覚醒王の噛みは口が主人公に重なる所が構え (2026-09-26・噛みは口の周りだけ当たる)。
  const contactGap = stanceBodyGap(boss, dir) ?? awokenBiteLandingGapPx(boss, attack)
    ?? Math.min(p.contact, Math.max(p.front + 15, initialGap))
  const advance = Math.max(0, Math.min(lungeDistance + BOSS.lungeCatchUp,
    (player.x - boss.x) * dir - contactGap))
  return inArena(boss.x + dir * advance, boss)
}
